import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaUser, FaPlus, FaStar, FaTshirt, FaGlobe, FaBirthdayCake, FaEye, FaHair, FaImage, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/backendConfig';
import { enqueueAndPoll } from '../utils/jobPolling';
import { useAuth } from '../context/AuthContext';
import CharacterPreviewModal from './CharacterPreviewModal';
import { checkContentModeration, getContentModerationMessage } from '../utils/contentModeration';
import ContentModerationModal from './ContentModerationModal';

const ROLES = [
  'Hero/Protagonist', 'Villain/Antagonist', 'Sidekick', 'Mentor', 
  'Love Interest', 'Comic Relief', 'Guardian', 'Rival', 'Friend', 'Family Member'
];

export default function EnhancedCharacterModal({ isOpen, onClose, onCharacterAdded, storyId }) {
  const { user } = useAuth();
  const [character, setCharacter] = useState({ 
    name: '', 
    age: '', 
    gender: '', 
    role: 'Hero/Protagonist', 
    appearance: '',
    countryOfOrigin: '',
    characterFace: ''
  });
  const [characterImage, setCharacterImage] = useState(null);
  const [isGeneratingCharacter, setIsGeneratingCharacter] = useState(false);
  const [characterCreationError, setCharacterCreationError] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [showContentModerationModal, setShowContentModerationModal] = useState(false);
  const [contentModerationMessage, setContentModerationMessage] = useState('');
  const [contentModerationKeywords, setContentModerationKeywords] = useState([]);

  const handleCreateCharacter = async () => {
    console.log('=== FUNCTION CALLED ===');
    console.log('=== FUNCTION CALLED ===');
    console.log('=== FUNCTION CALLED ===');
    console.log('character:', character);
    console.log('Validation check:', {
      name: !!character.name,
      age: !!character.age,
      gender: !!character.gender,
      appearance: !!character.appearance,
      countryOfOrigin: !!character.countryOfOrigin,
      characterFace: !!character.characterFace
    });
    
    if (!character.name || !character.age || !character.gender || !character.appearance || !character.countryOfOrigin || !character.characterFace) {
      console.log('=== VALIDATION FAILED ===');
      setCharacterCreationError('Please fill in all character details including country of origin and character face before creating the character.');
      return;
    }

    // Content moderation check
    const textToCheck = [
      character.name,
      character.age,
      character.gender,
      character.appearance,
      character.countryOfOrigin,
      character.characterFace,
      character.role
    ].join(' ');
    
    const moderationCheck = checkContentModeration(textToCheck);
    
    if (moderationCheck.isInappropriate) {
      const errorMessage = getContentModerationMessage('character', moderationCheck.matchedKeywords);
      setContentModerationMessage(errorMessage);
      setContentModerationKeywords(moderationCheck.matchedKeywords);
      setShowContentModerationModal(true);
      setCharacterCreationError(errorMessage);
      return;
    }

    if (!user) {
      alert('You must be logged in to create a character.');
      return;
    }

    setIsGeneratingCharacter(true);
    setCharacterCreationError(null);

    try {
      // Create a simple character object with only user-provided fields
      const characterData = {
        name: character.name,
        age: character.age,
        gender: character.gender,
        role: character.role,
        description: character.appearance, // Pass appearance as simple string
        countryOfOrigin: character.countryOfOrigin,
        characterFace: character.characterFace
      };

      // Non-blocking: enqueue job, poll until done (same as art history / ways of seeing).
      const data = await enqueueAndPoll(
        `${API_ENDPOINTS.backendBase}/api/characters/generate-image`,
        {
          characterData: { ...characterData, userId: user.uid },
          prompt: null,
          storyId: storyId,
        },
        { interval: 2000, maxAttempts: 150 }
      );
      setCharacterImage(data.imageUrl);
      setShowImagePreview(true);
    } catch (error) {
      console.error('Error creating character:', error);
      if (error.message && error.message.includes('Content moderation')) {
        setContentModerationMessage(error.message);
        setContentModerationKeywords([]);
        setShowContentModerationModal(true);
        setCharacterCreationError(error.message);
      } else {
        setCharacterCreationError('Failed to create character. Please try again.');
      }
    } finally {
      setIsGeneratingCharacter(false);
    }
  };

  const handleCharacterPreviewSave = async (updatedCharacter) => {
    try {
      const characterData = {
        ...updatedCharacter,
        id: Date.now(),
        imageUrl: updatedCharacter.imageUrl || characterImage,
        medium: 'oil painting'
      };
      
      // Note: Character is already saved to library by the backend during image generation
      // No need to save again here to avoid duplication
      
      onCharacterAdded(characterData);
      setCharacter({
        name: '', age: '', gender: '', role: 'Hero/Protagonist', appearance: '', countryOfOrigin: '', characterFace: ''
      });
      setCharacterImage(null);
      setShowImagePreview(false);
      onClose();
    } catch (error) {
      console.error('Error saving character:', error);
      setCharacterCreationError('Failed to save character. Please try again.');
    }
  };

  const handleCharacterPreviewEdit = async (editPrompt) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/edit-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          characterData: character,
          currentImageUrl: characterImage,
          editPrompt: editPrompt
        })
      });

      if (!response.ok) {
        throw new Error('Failed to edit character image');
      }

      const data = await response.json();
      setCharacterImage(data.imageUrl);
      return data.imageUrl;
    } catch (error) {
      console.error('Error editing character:', error);
      setCharacterCreationError('Failed to edit character. Please try again.');
      throw error;
    }
  };

  const handleCharacterPreviewRegenerate = async (prompt) => {
    try {
      const data = await enqueueAndPoll(
        `${API_ENDPOINTS.backendBase}/api/characters/generate-image`,
        {
          characterData: { ...character, userId: user?.uid },
          prompt,
          storyId,
        },
        { interval: 2000, maxAttempts: 150 }
      );
      setCharacterImage(data.imageUrl);
      return data.imageUrl;
    } catch (error) {
      console.error('Error regenerating character image:', error);
      setCharacterCreationError('Failed to regenerate character image. Please try again.');
      throw error;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-400 to-amber-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Create New Character</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {/* Character Creation Form */}
          <div className="bg-amber-50 rounded-xl p-6 border-2 border-amber-200">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-amber-800 mb-2">Character Name</label>
                <input
                  type="text"
                  value={character.name}
                  onChange={(e) => setCharacter({...character, name: e.target.value})}
                  placeholder="Enter character name..."
                  className="w-full p-3 border-2 border-amber-200 rounded-lg focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-amber-800 mb-2">Age</label>
                <input
                  type="number"
                  value={character.age}
                  onChange={(e) => setCharacter({...character, age: e.target.value})}
                  placeholder="Enter age..."
                  className="w-full p-3 border-2 border-amber-200 rounded-lg focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-amber-800 mb-2">Gender</label>
                <input
                  type="text"
                  value={character.gender}
                  onChange={(e) => setCharacter({...character, gender: e.target.value})}
                  placeholder="Enter gender..."
                  className="w-full p-3 border-2 border-amber-200 rounded-lg focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-amber-800 mb-2">Role in Story</label>
                <select
                  value={character.role}
                  onChange={(e) => setCharacter({...character, role: e.target.value})}
                  className="w-full p-3 border-2 border-amber-200 rounded-lg focus:border-amber-400 focus:outline-none"
                >
                  {ROLES.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-amber-800 mb-2">Country of Origin</label>
                <input
                  type="text"
                  value={character.countryOfOrigin}
                  onChange={(e) => setCharacter({...character, countryOfOrigin: e.target.value})}
                  placeholder="e.g., United States, Japan, France..."
                  className="w-full p-3 border-2 border-amber-200 rounded-lg focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-amber-800 mb-2">Define Character Face</label>
                <textarea
                  value={character.characterFace}
                  onChange={(e) => setCharacter({...character, characterFace: e.target.value})}
                  placeholder="Describe the character's facial features: eyes, nose, mouth, facial shape, expressions, etc."
                  className="w-full p-3 border-2 border-amber-200 rounded-lg focus:border-amber-400 focus:outline-none min-h-[80px]"
                />
              </div>
            </div>
            
            {/* Appearance field - full width */}
            <div className="mt-4">
              <label className="block text-sm font-semibold text-amber-800 mb-2">
                👤 Character Appearance
              </label>
              <textarea
                value={character.appearance}
                onChange={(e) => setCharacter({...character, appearance: e.target.value})}
                placeholder="Describe their appearance: hair color, clothing, distinctive features, etc..."
                rows="3"
                className="w-full p-3 border-2 border-amber-200 rounded-lg focus:border-amber-400 focus:outline-none"
              />
            </div>

            {/* Create Character Button */}
            <div className="mt-6 flex justify-center">
              <button
                onClick={handleCreateCharacter}
                disabled={isGeneratingCharacter || !character.name || !character.age || !character.gender || !character.appearance || !character.countryOfOrigin || !character.characterFace}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:from-gray-300 disabled:to-gray-400 text-white px-8 py-4 rounded-xl font-bold transition-all duration-300 flex items-center space-x-3 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
              >
                {isGeneratingCharacter ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    <span>Creating Character...</span>
                  </>
                ) : (
                  <>
                    <FaImage />
                    <span>Create Character</span>
                  </>
                )}
              </button>
            </div>

            {/* Error Display */}
            {characterCreationError && (
              <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {characterCreationError}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-amber-200 flex justify-end">
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-xl font-bold transition-all duration-300"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Character Preview Modal */}
      {showImagePreview && (
        <CharacterPreviewModal
          isOpen={showImagePreview}
          onClose={() => setShowImagePreview(false)}
          onSave={handleCharacterPreviewSave}
          onEdit={handleCharacterPreviewEdit}
          onRegenerate={handleCharacterPreviewRegenerate}
          character={character}
          imageUrl={characterImage}
          error={characterCreationError}
        />
      )}

      {/* Content Moderation Modal */}
      <ContentModerationModal
        isOpen={showContentModerationModal}
        onClose={() => setShowContentModerationModal(false)}
        message={contentModerationMessage}
        matchedKeywords={contentModerationKeywords}
      />
    </div>
  );
}

EnhancedCharacterModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCharacterAdded: PropTypes.func.isRequired,
  storyId: PropTypes.string
}; 